import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

// Common components
import AppBar from "../../../common/AppBar";
import Loader from "../../../common/Loader";
import Snackbar from "../../../common/Snackbar";
import {
  VersionHistoryPopup,
  getVersionHistoryAuditPreviewPath,
} from "../../../common/VersionHistoryPopup";
import { useVersionHistoryDataFromNodes } from "../../../common/VersionHistoryPopup/useVersionHistoryDataFromNodes.js";

// Redux actions
import { createVideo, updateVideo } from "../../../redux/videoSlice";
import { openPreview } from "../../../redux/previewSlice";

// Utilities
import { getAuditTrailData } from "../../../util/getAuditTrailData";
import { buildPermissionContext } from "../../../util/permissions";
import { cleanBaselineHtml } from "../../../util/lexical";
import { setTaskReference } from "../../Tasks/Uitlities";
import { pingTask } from "../../../util/apiService";

// Custom hooks
import useVideoData from "../hooks/useVideoData";
import { useUnsavedChangesBlocker } from "../../../hooks/useUnsavedChangesBlocker";

// Video form fields component
import VideoFormFields from "./VideoFormFields";
import { BASE_URL } from "../../../config";

/** Global "Video N" index — same traversal as ViewChildNode / ChildrenRenderer (`video`). */
function computeGlobalMediaOrdinalForVideo({
  allRevisionNodes,
  section_id_ref,
  step_id_ref,
  child_node_id,
}) {
  const nodeType = "video";
  if (
    !child_node_id ||
    !Array.isArray(allRevisionNodes) ||
    !section_id_ref ||
    !step_id_ref
  ) {
    return null;
  }

  const compareSiblingOrders = (a, b) => {
    const intA = parseInt(a, 10);
    const intB = parseInt(b, 10);
    return intA - intB;
  };

  const sortedSections = [...allRevisionNodes].sort((a, b) => {
    const orderA = a.sibling_order || a.procedure_tree?.sibling_order;
    const orderB = b.sibling_order || b.procedure_tree?.sibling_order;
    const orderCompare = compareSiblingOrders(orderA, orderB);
    if (orderCompare !== 0) return orderCompare;
    const dateA = new Date(a.created_at);
    const dateB = new Date(b.created_at);
    return dateB - dateA;
  });

  let counter = 0;
  const matchesId = (c) =>
    c.tree_node_id === child_node_id ||
    c.detail?.canvas_tree_node_id === child_node_id ||
    c.detail?.video_tree_node_id === child_node_id ||
    c.detail?.element_id === child_node_id ||
    c.element_id === child_node_id;

  for (let i = 0; i < sortedSections.length; i++) {
    const section = sortedSections[i];
    for (let j = 0; j < (section.steps?.length || 0); j++) {
      const step = section.steps[j];
      const stepChildren =
        step.children?.filter((c) => c.type === nodeType) || [];

      const currentStepChild = stepChildren.find(matchesId);
      if (currentStepChild) {
        const sortedStepChildren = [...stepChildren].sort((a, b) => {
          const orderA = a.sibling_order || 0;
          const orderB = b.sibling_order || 0;
          return orderA - orderB;
        });
        const currentChildIndex = sortedStepChildren.findIndex(matchesId);
        counter += currentChildIndex;
        return counter + 1;
      }
      counter += stepChildren.length;

      for (let k = 0; k < (step.substeps?.length || 0); k++) {
        const substep = step.substeps[k];
        const substepChildren =
          substep.children?.filter((c) => c.type === nodeType) || [];
        const currentSubstepChild = substepChildren.find(matchesId);
        if (currentSubstepChild) {
          const sortedSubstepChildren = [...substepChildren].sort((a, b) => {
            const orderA = a.sibling_order || 0;
            const orderB = b.sibling_order || 0;
            return orderA - orderB;
          });
          const currentChildIndex =
            sortedSubstepChildren.findIndex(matchesId);
          counter += currentChildIndex;
          return counter + 1;
        }
        counter += substepChildren.length;
      }
    }
  }
  return counter + 1;
}

/**
 * VideoForm - Optimized video form component
 *
 * Key improvements:
 * - Custom hooks for data management
 * - Reduced location state dependencies
 * - Better error handling
 * - Cleaner state management
 * - Improved persistence strategy
 */
const VideoForm = ({
  // Core props
  mode,

  // URL parameters
  partnumber,
  revision,
  partId,
  revisionId,
  section_id_ref,
  step_id_ref,
  substep_id_ref,
  node_id_ref,
  video_id_ref,
  // Version history
  fromVersionHistory,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const authUser = useSelector((state) => state.auth.user);
  const permissionCtx = buildPermissionContext(authUser);
  const canEdit = permissionCtx.permissions?.Edit === true;

  // Use custom hook for data management
  const {
    loading,
    videoData,
    versionId,
    procedureId,
    lastSavedText,
    pageTitle,
    breadcrumbText,
    siblingOrder,
    allNodesData,
    sectionId,
    stepId,
    substepId,
    currentStatus,
    item_code_type,
  } = useVideoData({
    mode,
    id: video_id_ref,
    partnumber,
    revision,
    partId,
    revisionId,
    section_id_ref,
    step_id_ref,
    substep_id_ref,
    node_id_ref,
  });

  // Debug: Log videoData changes
  useEffect(() => {
  }, [loading, videoData]);

  // Ping API call every minute to keep lock alive
  useEffect(() => {
    if (!revisionId || !authUser) return;

    const pingInterval = setInterval(async () => {
      try {
        await pingTask({
          task_id: revisionId,
          user_id: authUser.id,
        });
      } catch (error) {
        console.error('Ping failed in VideoForm:', error);
      }
    }, 60000); // 60 seconds = 1 minute

    return () => {
      clearInterval(pingInterval);
    };
  }, [revisionId, authUser]);

  // Local state for video form
  const [isLoading, setIsLoading] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [comment, setComment] = useState("");

  // Video form state
  const [videoFile, setVideoFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [shortDescription, setShortDescription] = useState("");
  const [id, setId] = useState("");
  const [videoKey, setVideoKey] = useState(0);
  const [videoRemoved, setVideoRemoved] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "",
  });

  const videoRouteChildId =
    videoData?.tree_node_id || video_id_ref || null;

  const videoVersionHistoryMatchIds = useMemo(() => {
    const fromAttachments = Array.isArray(videoData?.detail?.attachments)
      ? videoData.detail.attachments.flatMap((a) =>
          [a?.attachment_tree_node_ref, a?.attachment_id].filter(Boolean),
        )
      : [];
    return [
      video_id_ref,
      videoData?.tree_node_id,
      videoData?.detail?.procedure_tree_ref,
      videoData?.attachment_id_ref,
      videoData?.attachment_tree_node_id,
      videoData?.detail?.attachment_id,
      videoData?.detail?.attachment_tree_node_id,
      videoData?.detail?.video_tree_node_id,
      videoData?.detail?.rich_text_node_id,
      videoData?.detail?.rich_text_tree_node_id,
      videoData?.detail?.canvas_tree_node_id,
      videoData?.detail?.canva_tree_node_id,
      videoData?.detail?.form_tree_node_id,
      videoData?.detail?.element_id,
      ...fromAttachments,
    ].filter(Boolean);
  }, [
    video_id_ref,
    videoData?.tree_node_id,
    videoData?.detail?.procedure_tree_ref,
    videoData?.attachment_id_ref,
    videoData?.attachment_tree_node_id,
    videoData?.detail?.attachment_id,
    videoData?.detail?.attachment_tree_node_id,
    videoData?.detail?.video_tree_node_id,
    videoData?.detail?.rich_text_node_id,
    videoData?.detail?.rich_text_tree_node_id,
    videoData?.detail?.canvas_tree_node_id,
    videoData?.detail?.canva_tree_node_id,
    videoData?.detail?.form_tree_node_id,
    videoData?.detail?.element_id,
    videoData?.detail?.attachments,
  ]);

  const videoNodeHistoryLeafOrdinal = useMemo(() => {
    const fromTitle =
      typeof pageTitle === "string"
        ? pageTitle.match(/Video\s+(\d+)/i)
        : null;
    if (fromTitle) {
      const n = parseInt(fromTitle[1], 10);
      if (Number.isFinite(n) && n >= 1) return n;
    }
    const fromTraversal = computeGlobalMediaOrdinalForVideo({
      allRevisionNodes: allNodesData,
      section_id_ref,
      step_id_ref,
      child_node_id: videoRouteChildId,
    });
    return fromTraversal != null && Number.isFinite(Number(fromTraversal))
      ? Number(fromTraversal)
      : undefined;
  }, [
    pageTitle,
    allNodesData,
    section_id_ref,
    step_id_ref,
    videoRouteChildId,
  ]);

  const {
    mergedVersionHistories,
    allVersionHistories,
    versionHistoryTableLoading,
  } = useVersionHistoryDataFromNodes({
    isOpen: showVersionHistory && mode === "edit",
    revisionId,
    partId,
    allRevisionNodes: allNodesData,
    fallbackVersionId:
      videoData?.detail?.version_id_ref || videoData?.version_id_ref || null,
    nodesReady:
      mode === "edit" &&
      !loading &&
      Array.isArray(allNodesData) &&
      allNodesData.length > 0,
  });

  const versionHistories = mergedVersionHistories;
  const versionHistoryLoading = versionHistoryTableLoading;

  // Initialize form fields with existing video data in edit mode
  useEffect(() => {
    if (mode === "edit" && videoData) {
      // Process short description to handle both plain text and HTML content
      let processedShortDescription = videoData.detail.short_description || "";
      if (processedShortDescription && !processedShortDescription.includes('<div class="editor-shell">')) {
        // If it's plain text (no editor-shell wrapper), convert it to HTML format
        processedShortDescription = `<p>${processedShortDescription}</p>`;
      }
      
      // Set form fields from existing video data
      setShortDescription(cleanBaselineHtml(processedShortDescription));
      setVideoUrl(videoData.detail.video_url || "");
      setId(videoData.tree_node_id || "");
      setVideoRemoved(false);

      // Set preview if video URL exists
      if (videoData.detail?.video) {
        setPreview(videoData.detail.video);
      } else if (videoData.detail?.videos?.[0]?.video) {
        setPreview(videoData.detail.videos[0].video);
      }
    } else {
    }
  }, [mode, videoData]);

  // Tracks changes to non-editor fields (file upload, URL, ID, etc.)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Undo-based dirty tracking for the Lexical short-description editor.
  const [isShortDescriptionDirty, setIsShortDescriptionDirty] = useState(false);
  const handleShortDescriptionDirtyChange = useCallback(
    (dirty) => setIsShortDescriptionDirty(dirty),
    []
  );

  // Navigation protection — block leaving when any field has unsaved changes
  useUnsavedChangesBlocker((hasUnsavedChanges || isShortDescriptionDirty) && !isLoading);

  // Snackbar handlers
  const showSnackbar = (message, type) => {
    setSnackbar({ open: true, message, type });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // AppBar back handler
  const handleAppBarBack = useCallback(() => {
    window.history.back();
  }, []);

  // Upload to S3 helper function
  const uploadToS3 = async (data, fileName, fileType) => {
    try {
      // Get authorization token from localStorage or other storage
      const token =
        localStorage.getItem("token") ||
        localStorage.getItem("access_token") ||
        localStorage.getItem("auth_token") ||
        sessionStorage.getItem("token") ||
        "";

      if (!token) {
        throw new Error("No authorization token found");
      }

      // 1. Get presigned URL from backend
      const response = await fetch(
        `${BASE_URL}upload/get-upload-urls`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            folder: "video",
            files: [{ fileName, fileType }],
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to get upload URL: ${response.status}`);
      }

      const responseData = await response.json();
      const { uploadUrl, fileUrl } = responseData.files[0];

      // 2. Convert to blob if needed
      let blob;

      if (data instanceof Blob || data instanceof File) {
        blob = data;
      } else if (typeof data === "string") {
        if (!data.startsWith("data:")) {
          throw new Error("Invalid data format");
        }

        try {
          const base64Response = await fetch(data);
          if (!base64Response.ok) {
            throw new Error(
              `Failed to convert base64 to blob: ${base64Response.status}`
            );
          }
          blob = await base64Response.blob();
        } catch (blobError) {
          throw new Error(`Base64 conversion failed: ${blobError.message}`);
        }
      } else {
        throw new Error("Invalid data type provided");
      }

      // Validate blob size
      if (blob.size === 0) {
        throw new Error("Generated blob is empty");
      }

      const sizeInMB = blob.size / (1024 * 1024);
      if (sizeInMB > 250) {
        throw new Error(`File too large: ${sizeInMB.toFixed(2)}MB (max 250MB)`);
      }

      // 3. Upload to S3 using presigned URL with timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 300000); // 5 minutes for video uploads

      try {
        const s3Response = await fetch(uploadUrl, {
          method: "PUT",
          headers: {
            "Content-Type": fileType,
          },
          body: blob,
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (!s3Response.ok) {
          throw new Error(`S3 upload failed: ${s3Response.status}`);
        }
      } catch (fetchError) {
        clearTimeout(timeoutId);
        if (fetchError.name === "AbortError") {
          throw new Error("S3 upload timed out after 5 minutes");
        }
        throw fetchError;
      }

      return fileUrl;
    } catch (error) {
      throw error;
    }
  };

  // Video form handlers
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileUrl = URL.createObjectURL(file);
    setVideoFile(file);
    setPreview(fileUrl);
    setVideoRemoved(false);
    setVideoKey((prev) => prev + 1);
    setHasUnsavedChanges(true);
  };

  const handleDeleteClick = () => {
    setVideoFile(null);
    setPreview("");
    setVideoRemoved(true);
    setVideoKey((prev) => prev + 1);
    setHasUnsavedChanges(true);
  };

  const handleVideoUrlChange = (e) => {
    const newUrl = e.target.value;
    setVideoUrl(newUrl);
    setHasUnsavedChanges(true);
  };

  const handleShortDescriptionChange = (value) => {
    // Handle both event objects (from TextArea) and direct values (from ShortDescriptionEditor)
    const newValue = typeof value === 'object' && value.target ? value.target.value : value;
    
    // Clean the HTML content if it's from the rich text editor
    const cleanedValue = typeof newValue === 'string' && newValue.includes('<') ? 
      cleanBaselineHtml(newValue) : newValue;
    
    setShortDescription(cleanedValue);
  };

  const handleIdChange = (e) => {
    setId(e.target.value);
    setHasUnsavedChanges(true);
  };

  // Save handlers
  // NOTE: videoRemoved only controls whether the video FILE is kept; it must NOT gate the URL field.
  const hasActiveVideoUrl = () => Boolean(videoUrl?.trim());

  const handleSaveClick = () => {
    if (!videoFile && !hasActiveVideoUrl() && !preview) {
      showSnackbar(
        "Please upload a video file or provide a video URL before submitting.",
        "error"
      );
      return;
    }
    setShowModal(true);
  };

  const handleCloseModal = () => setShowModal(false);

  const handleConfirmSave = async () => {
    if (!comment) {
      showSnackbar("Comment is mandatory.", "error");
      return;
    }

    const activeVideoUrl = hasActiveVideoUrl() ? videoUrl.trim() : "";

    if (!videoFile && !activeVideoUrl && !preview) {
      showSnackbar(
        "Please upload a video file or provide a video URL before submitting.",
        "error"
      );
      return;
    }

    setIsLoading(true);

    try {
      // Determine the video URL for the 'video' field
      // This should be: S3 URL if file uploaded, existing video URL if in edit mode, or empty
      let finalVideoUrl = "";

      if (videoFile) {
        // File uploaded - upload to S3
        try {
          // Generate unique filename for S3 upload
          const timestamp = new Date().getTime();
          const fileExtension = videoFile.name.split(".").pop() || "mp4";
          const fileName = `video-${timestamp}.${fileExtension}`;
          const fileType = videoFile.type || `video/${fileExtension}`;

          // Upload to S3 and get the URL
          finalVideoUrl = await uploadToS3(videoFile, fileName, fileType);

        } catch (uploadError) {
          console.error("❌ Error uploading video to S3:", uploadError);
          showSnackbar(
            "Error uploading video to S3: " +
            (uploadError?.message || "Unknown error"),
            "error"
          );
          setIsLoading(false);
          return;
        }
      } else if (mode === "edit" && videoData && !videoRemoved) {
        // Edit mode with no new file - use existing video URL
        // Check if there's an existing video from videoData
        if (videoData.detail?.videos?.[0]?.video) {
          finalVideoUrl = videoData.detail.videos[0].video;
        } else if (videoData.detail?.video) {
          finalVideoUrl = videoData.detail.video;
        } else if (preview) {
          // Fallback to preview if available
          finalVideoUrl = preview;
        }
      } else if (preview && !videoFile) {
        // Add mode with preview URL (from URL field or existing)
        finalVideoUrl = preview;
      }

      // Create version_content for the API call
      const version_content = {
        action_to_show: `Save ${pageTitle} for ${partnumber}, Rev ${revision}`,
        partNumber: partnumber,
        revision,
        status: currentStatus,
        item_code_type,
        type: "video",
        procedure_revision_id: revisionId,
      };

      // Use video slice to create or update video
      let response;
      if (mode === "edit") {
        // Edit mode - use updateVideo with JSON payload
        // Create version_content for update audit trail
        const updateVersionContent = {
          action_to_show: `Update ${pageTitle} for ${partnumber}, Rev ${revision}`,
          partNumber: partnumber,
          revision,
          status: currentStatus,
          item_code_type,
          type: "video",
          procedure_revision_id: revisionId,
          tree_node_id: videoData?.tree_node_id,
          video_tree_node_id: videoData?.detail?.video_tree_node_id,
          element_id:
            videoData?.detail?.element_id ?? videoData?.element_id,
          procedure_tree_ref:
            videoData?.detail?.procedure_tree_ref ??
            videoData?.procedure_tree_ref,
        };

        response = await dispatch(
          updateVideo({
            id: videoData?.detail?.video_tree_node_id,
            jsonData: {
              short_description: shortDescription,
              revision_id_ref: revisionId,
              section_id_ref: sectionId,
              part_required_id_ref: partId,
              step_id_ref: stepId,
              video_url: activeVideoUrl,
              substep_id_ref:
                substepId === "null" || substepId === "undefined"
                  ? ""
                  : substepId,
              sibling_order: (
                videoData.sibling_order ||
                videoData.sibling_order?.toString() ||
                "1"
              ).toString(),
              video: finalVideoUrl || "",
              video_id: videoData?.detail?.videos[0]?.video_id,
            },
            comment: comment || `Updated ${pageTitle}`,
            version_content: updateVersionContent,
            allNodesData,
            partnumber,
            revision,
            tree_node_id: videoData?.tree_node_id,
            parent_version:
              versionId != null && String(versionId).trim() !== ""
                ? String(versionId).trim()
                : null,
          })
        ).unwrap();
        showSnackbar("Video updated successfully!", "success");
      } else {
        // Add mode - use createVideo with JSON payload
        response = await dispatch(
          createVideo({
            jsonData: {
              short_description: shortDescription,
              revision_id_ref: revisionId,
              section_id_ref: sectionId,
              part_required_id_ref: partId,
              step_id_ref: stepId,
              video_url: activeVideoUrl,
              substep_id_ref:
                substepId === "null" || substepId === "undefined"
                  ? ""
                  : substepId,
              sibling_order: (siblingOrder || 1).toString(),
              video: finalVideoUrl || "",
            },
            comment,
            version_content,
            allNodesData,
            partnumber,
            revision,
          })
        ).unwrap();
        showSnackbar("Video saved successfully!", "success");
      }

      setShowModal(false);

      navigate(
        `/view-childnode/${partnumber}/${revision}/${partId}/${revisionId}/${section_id_ref}/${step_id_ref}/${response.results.procedure_tree_ref
        }${substep_id_ref ? `?substep_id_ref=${substep_id_ref}` : ""}`
      );
    } catch (error) {
      console.error("❌ Error saving video:", error);
      showSnackbar(
        "Error saving video: " + (error?.message || "Unknown error"),
        "error"
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Preview functionality
  const handlePreviewClick = async () => {
    const existingVideoAvailable =
      !videoRemoved &&
      (videoData?.detail?.videos?.[0]?.video || videoData?.detail?.video);

    const activeVideoUrl = hasActiveVideoUrl() ? videoUrl.trim() : "";

    if (
      !videoFile &&
      !activeVideoUrl &&
      !preview &&
      !existingVideoAvailable
    ) {
      showSnackbar("Please fill the mandatory fields.", "error");
      return;
    }

    try {
      // Convert video file to base64 for preview (if file uploaded)
      let videoBase64 = "";
      if (videoFile) {
        videoBase64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(videoFile);
        });
      }

      // Determine the video source for both detail.video (string) and detail.videos (array)
      let videoSource = null;
      let videosArray = [];

      if (videoFile) {
        // New file uploaded - use base64
        videoSource = videoBase64;
        videosArray = [
          {
            video: videoBase64,
            video_id:
              mode === "edit" ? videoData?.detail?.videos?.[0]?.video_id : null,
          },
        ];
      } else if (preview) {
        // Preview URL exists (from existing video or URL field)
        videoSource = preview;
        videosArray = [
          {
            video: preview,
            video_id:
              mode === "edit" ? videoData?.detail?.videos?.[0]?.video_id : null,
          },
        ];
      } else if (activeVideoUrl) {
        // User provided video URL
        videoSource = activeVideoUrl;
        videosArray = [
          {
            video: activeVideoUrl,
            video_id:
              mode === "edit" ? videoData?.detail?.videos?.[0]?.video_id : null,
          },
        ];
      } else if (existingVideoAvailable && videoData?.detail?.videos?.[0]?.video) {
        // Edit mode - use existing video
        videoSource = videoData.detail.videos[0].video;
        videosArray = videoData.detail.videos;
      }

      // Construct draftChild object for video preview
      const draftChild = {
        tree_node_id: mode === "edit" ? id : `draft-video-${Date.now()}`,
        type: "video",
        mode: mode, // "add" or "edit"
        isDraft: true,
        sibling_order: (mode === "add"
          ? siblingOrder || 1
          : videoData?.sibling_order || 1
        ).toString(),
        section_id_ref: section_id_ref,
        step_id_ref: step_id_ref,
        substep_id_ref:
          substep_id_ref === "null" || substep_id_ref === "undefined"
            ? null
            : substep_id_ref,
        detail: {
          short_description: shortDescription,
          video_url: activeVideoUrl,
          // Set detail.video (string) for VideoNode component compatibility
          video: videoSource || "",
          // Also keep detail.videos (array) for API compatibility
          videos: videosArray,
        },
      };

      // Dispatch preview with draft video
      dispatch(
        openPreview({
          partId: partId,
          revisionId: revisionId,
          draftChild: draftChild,
        })
      );
    } catch (error) {
      console.error("Error preparing video preview:", error);
      showSnackbar("Error preparing preview: " + error.message, "error");
    }
  };

  // Set task reference for edit mode
  useEffect(() => {
    if (mode === "edit" && videoData) {
      setTaskReference(
        dispatch,
        `Video ${partnumber}`,
        videoData?.detail?.video_tree_node_id,
        videoData?.tree_node_id,
        videoData?.ltree_path
      );
    }
  }, [mode, videoData, dispatch, partnumber]);

  if (loading) {
    return <Loader text="Loading video data..." />;
  }

  return (
    <div>
      <AppBar
        onBack={handleAppBarBack}
        subTitleAboveTitle={
          <>
            {localStorage.getItem("user_name")} is{" "}
            {mode === "add" ? "Creating" : "Editing"}
          </>
        }
        title={pageTitle}
        showTablesDropdown={true}
        showProfileButton={fromVersionHistory ? false : true}
        buttons={
          fromVersionHistory
            ? []
            : [
              { label: "Preview", onClick: handlePreviewClick },
              {
                label: "Save",
                onClick: canEdit ? handleSaveClick : undefined,
                disabled: !canEdit,
                title: !canEdit
                  ? "You do not have permission to save videos."
                  : undefined,
              },
            ]
        }
        subheading={breadcrumbText}
        showBlackTitle={true}
        showColorTitle={false}
        subheadingAfterTitle={mode === "edit" ? lastSavedText : undefined}
        versionHistoryHandler={
          mode === "edit" ? () => setShowVersionHistory(true) : null
        }
      />

      <VideoFormFields
        mode={mode}
        preview={preview}
        videoKey={`video-${videoKey}`}
        onDeleteClick={handleDeleteClick}
        onFileChange={handleFileUpload}
        fileAccept="video/mp4,video/x-matroska,video/quicktime"
        fileCapture="environment"
        idValue={id}
        onIdChange={handleIdChange}
        videoUrl={videoUrl}
        onVideoUrlChange={handleVideoUrlChange}
        shortDescription={shortDescription}
        onShortDescriptionChange={handleShortDescriptionChange}
        onShortDescriptionDirtyChange={handleShortDescriptionDirtyChange}
        maxSizeMb={250}
        canEdit={canEdit}
        saveModal={{
          isOpen: showModal,
          onClose: handleCloseModal,
          onSave: handleConfirmSave,
          title: `${mode === "add" ? "Save" : "Update"} ${pageTitle}`,
          subtitle: breadcrumbText,
          editorName: localStorage.getItem("user_name") || "Unknown User",
          comment: comment,
          onCommentChange: (e) => setComment(e.target.value),
          isLoading: isLoading,
          showLoader: true,
        }}
      />

      {isLoading && (
        <Loader
          text={mode === "add" ? "Saving video..." : "Updating video..."}
        />
      )}

      {snackbar.open && (
        <Snackbar
          message={snackbar.message}
          type={snackbar.type}
          onClose={handleCloseSnackbar}
        />
      )}

      {showVersionHistory && (
        <VersionHistoryPopup
          onClose={() => setShowVersionHistory(false)}
          procedurePartNumber={partnumber}
          procedureRevision={revision}
          versionHistories={versionHistories}
          mode={mode}
          versionId={versionId}
          nodeId={videoData?.tree_node_id || video_id_ref || ""}
          nodeIdField="video_tree_node_id"
          breadcrumbText={breadcrumbText}
          revisionTreeNodes={allNodesData}
          nodeHistoryMatchIds={
            Array.isArray(videoVersionHistoryMatchIds) &&
            videoVersionHistoryMatchIds.length > 0
              ? videoVersionHistoryMatchIds
              : undefined
          }
          nodeHistoryLeafMediaOrdinal={
            videoNodeHistoryLeafOrdinal != null &&
            Number.isFinite(Number(videoNodeHistoryLeafOrdinal))
              ? Number(videoNodeHistoryLeafOrdinal)
              : undefined
          }
          nodeHistoryLeafMediaPhraseRegex="Video"
          procedureRevisionIdForNodeHistory={revisionId}
          restrictNodeHistoryToProcedureRevision={
            allVersionHistories.length === 0
          }
          emptyMessage={
            mode === "add"
              ? "No version history available for new videos"
              : "No version history available"
          }
          initialFilterType="versions_for_this_page"
          onViewDetails={(row) => {
            const aid = row.audit_trail_id || row.audit_trail_id_ref;
            if (!aid) return;
            const path = getVersionHistoryAuditPreviewPath(
              aid,
              row.action || row.action_to_show
            );
            if (!path) return;
            setShowVersionHistory(false);
            navigate(path);
          }}
          isRowViewActionDisabled={(row) =>
            !(row.audit_trail_id || row.audit_trail_id_ref)
          }
          exportCsvDisabled={versionHistoryLoading}
        />
      )}
    </div>
  );
};

export default VideoForm;
